import { useEffect, useRef, useState } from 'react';
import './Experience.css';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const experiences = [
  {
    company: 'Volvo Group',
    role: 'Data Scientist',
    type: 'Alternance',
    period: 'sept. 2026 - aujourd\'hui',
    location: 'Lyon, France · Sur site',
    description:
      'Passage du développement applicatif à la data science au sein du groupe. Construction de pipelines de données (collecte, nettoyage, feature engineering) et entraînement de modèles de machine learning supervisé sur les données véhicules et industrielles : maintenance prédictive, détection d\'anomalies et classification. Évaluation des modèles (validation croisée, précision/rappel, dérive), puis industrialisation : versioning des expériences, exposition via API et suivi en production.',
    tags: ['Python', 'Machine Learning', 'pandas', 'scikit-learn', 'PyTorch', 'MLOps'],
  },
  {
    company: 'Volvo Group',
    role: 'Ingénieur Logiciel',
    type: 'Alternance',
    period: 'août 2025 - août 2026',
    location: 'Lyon, France · Sur site',
    description:
      'Intervention sur des applications Java au sein d\'une équipe Agile avec un fort focus sur la sécurité applicative. Intégration de tests dans les pipelines CI/CD selon les recommandations du Golden Path Volvo, reprise et déploiement d\'un POC dédié aux vulnérabilités, et participation aux migrations WebSphere → JBoss 7 → JBoss 8.',
    tags: ['Java', 'CI/CD', 'Sécurité applicative', 'JBoss', 'Agile'],
  },
  {
    company: 'Keysper',
    role: 'Architecte Systèmes & Développeur Fullstack',
    type: 'Alternance',
    period: 'janv. 2025 - août 2025',
    location: 'Bordeaux, France · Hybride',
    description:
      'Refonte complète de la plateforme immobilière pour étudiants : création d\'une app mobile native avec un système de swipe inspiré des apps de rencontre, gestion multi-profils avec coffres-forts numériques, et sécurisation de l\'API (migration TypeScript, JWT, Bcrypt).',
    tags: ['React Native', 'TypeScript', 'Node.js', 'Express.js', 'JWT', 'Bcrypt'],
  },
  {
    company: 'Groupe KDE',
    role: 'Développeur Applications',
    type: 'CDD',
    period: 'juin 2024 - sept. 2024',
    location: 'Pau, France · Sur site',
    description:
      'Développement d\'une application de gestion interne pour une entreprise de transport (130 employés, 120 véhicules). Suivi des itinéraires, taux de réussite des livreurs, état des véhicules (système WeProov), et interface admin avec droits différenciés. Sécurisation par requêtes préparées et triggers SQL.',
    tags: ['PHP', 'JavaScript', 'SQL', 'HTML/CSS', 'MVC'],
  },
  {
    company: 'SitInCloud',
    role: 'Développeur Applications',
    type: 'Stage',
    period: 'janv. 2024 - févr. 2024',
    location: 'Pau, Helioparc · Sur site',
    description:
      'Conception du moteur de recherche Owlyscan en équipe de 4, selon une méthodologie Agile (sprints, daily, rétrospectives). Projet aujourd\'hui commercialisé et pleinement opérationnel.',
    tags: ['Vue.js', 'JavaScript', 'HTML/CSS', 'Linux', 'Bitbucket', 'Agile'],
  },
  {
    company: 'SIAME (IVAR Group / EDF)',
    role: 'Développeur Front-end',
    type: 'Stage',
    period: 'mai 2023 - juin 2023',
    location: 'Pau, France · Sur site',
    description:
      'Développement d\'une IHM temps réel pour des systèmes de chauffage industriels (brûleur, générateur de vapeur, compresseur). Surveillance continue des valeurs d\'entrée avec alertes de sécurité en cas de dépassement seuils critiques. Mission étendue ensuite pour EDF.',
    tags: ['JavaScript', 'HTML/CSS', 'Matlab', 'GitHub'],
  },
];

function ExperienceItem({ item, index }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`exp-item${inView ? ' is-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="exp-item__left">
        <span className="exp-item__period">{item.period}</span>
      </div>
      <div className="exp-item__line" aria-hidden="true">
        <div className="exp-item__dot" />
      </div>
      <div className="exp-item__right">
        <p className="exp-item__period exp-item__period--mobile">{item.period}</p>
        <p className="exp-item__company">{item.company}</p>
        <h3 className="exp-item__role">{item.role}</h3>
        <p className="exp-item__meta">{item.type} · {item.location}</p>
        <p className="exp-item__desc">{item.description}</p>
        <div className="exp-item__tags">
          {item.tags.map(t => (
            <span key={t} className="skill-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Experience() {
  return (
    <section className="experience section" id="experience">
      <p className="section__label">Expériences</p>
      <h2 className="section__title">
        Mon <span className="keyword">parcours</span>
      </h2>
      <div className="exp-list">
        {experiences.map((item, i) => (
          <ExperienceItem key={i} item={item} index={i} />
        ))}
      </div>
    </section>
  );
}
